import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { askMentorAI, getChatById } from '../apis/api-ai'
import { MentorAIRequest, MentorAIResponse, ChatHistory } from '../types/mentorTypes'
import { useZoomContext } from '../context/ZoomContext'
import { useEffect, useState } from 'react'
import { useCourseStudyPlan } from './useCourseStudyPlan'
import { useSessionParams } from './useSessionParams'
import { useTranslation } from 'react-i18next'
import { useUserCourses } from '../pages/meeting-view/hooks/useCourses'

/**
 * Custom hook to interact with the Mentor AI API
 * Uses react-query's useMutation for managing API state
 */
export const useMentorAIQuery = () => {
    const { t } = useTranslation('mentor')
    const [chatId, setChatId] = useState<string>()
    const { currentSessionInfo } = useZoomContext()
    const { _classId: courseId, room } = useSessionParams()
    const { currentSessionId } = useCourseStudyPlan(courseId)
    const { currentCourse } = useUserCourses()
    const enableSharedContent = !!currentCourse?.courseFlags.enableSharedContent

    const queryClient = useQueryClient()
    const MENTOR_QUERY_KEY = 'mentorAI'
    const CHAT_HISTORY_KEY = 'chatHistory'

    useEffect(() => {
        if (currentSessionInfo?.topic && currentSessionId) {
            const newChatId = `${currentSessionInfo?.topic}-${currentSessionId}`
            setChatId(newChatId)
            console.log('📣 Setting chatId in useMentorAIQuery:', newChatId)
        }
    }, [currentSessionInfo, currentSessionId])

    // Set up mutation with react-query
    const { mutateAsync: askMentor, isPending: isAskingMentor } = useMutation({
        mutationFn: async (request: MentorAIRequest): Promise<MentorAIResponse> => {
            try {
                // Call the API and return the response
                const response = await askMentorAI(request)
                return response
            } catch (err) {
                console.error('❌ Error calling Mentor AI API:', err)
                throw err
            }
        },
        // Invalidate relevant queries when mutation is successful
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [MENTOR_QUERY_KEY] })
        },
    })

    /**
     * React Query hook to fetch chat history
     * @param chatId The unique identifier for the chat
     * @param options Additional options for the query
     * @returns React Query result with chat history data
     */
    const { data: chatHistory, ...rest } = useQuery<ChatHistory>({
        queryKey: [CHAT_HISTORY_KEY, chatId],
        queryFn: async () => {
            try {
                return await getChatById(chatId!)
            } catch (error: any) {
                // If it's a 404, return the placeholder data instead of throwing
                if (error?.response?.status === 404) {
                    return {
                        history: [
                            {
                                role: 'assistant',
                                content: t('welcomeMessage', 'Welcome to the Mentor AI!'),
                                name: t('mentorName', 'Mentor AI'),
                            },
                        ],
                        chatId: '1234567890',
                    }
                }
                // For other errors, rethrow them
                throw error
            }
        },
        enabled: !!chatId && !!enableSharedContent && room === 'teams',
        placeholderData: prev =>
            prev || {
                history: [
                    {
                        role: 'assistant',
                        content: t('welcomeMessage', 'Welcome to the Mentor AI!'),
                        name: t('mentorName', 'Mentor AI'),
                    },
                ],
                chatId: '1234567890',
            },
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        staleTime: 0, // Ensure we always fetch fresh data
        retry: false,
    })

    const invalidateChatHistory = (commandChannelChatId: string) => {
        if (!commandChannelChatId) {
            console.error('❌ Cannot invalidate chat history: chatId is undefined')
            return
        }
        console.log('📣 Invalidating chat history for chatId:', commandChannelChatId)
        queryClient.invalidateQueries({ queryKey: [CHAT_HISTORY_KEY, commandChannelChatId] })
    }

    return {
        askMentor,
        isAskingMentor,
        MENTOR_QUERY_KEY,
        CHAT_HISTORY_KEY,
        invalidateChatHistory,
        ...rest,
        ...chatHistory,
    }
}
